import Chart from "~/components/analytics/shared/Chart";
import set from "lodash/set";
import { getMicrosecondDurationFormattedString } from "~/lib/date";
import { Metrics, ChartData } from "~/components/analytics/shared/type";

type Props = {
  label: Metrics["label"];
  metric: Metrics["metric"];
  chartType: Metrics["chartType"];
  series: ChartData;
};

const TIMED_METRICS = [
  "p50_duration",
  "p95_duration",
  "duration",
  "total_duration",
  "average_execution_duration",
  "speed",
];

const formatReliability = (reliability) => {
  if (reliability === undefined) {
    return;
  }

  return `${Number.parseFloat(reliability).toFixed(2)}%`;
};

export const chartOptions = (metric: Props["metric"]) => {
  const options = {};

  const formatTooltip = (value, format) => {
    const label = value.dataset?.label ? `${value.dataset.label}: ` : "";
    return value.parsed.y == null ? `${label}No data available for this day` : `${label}${format}`;
  };

  if (TIMED_METRICS.includes(metric)) {
    set(options, "options.scales.y.ticks", {
      callback: (value) => getMicrosecondDurationFormattedString(value * 1_000_000),
    });
    set(options, "options.plugins.tooltip.callbacks", {
      label: (value) =>
        formatTooltip(value, getMicrosecondDurationFormattedString(value.parsed.y * 1_000_000)),
    });
  } else if (metric === "reliability") {
    set(options, "options.scales.y.ticks", {
      callback: (value) => `${value}%`,
    });
    set(options, "options.plugins.tooltip.callbacks", {
      label: (value) => formatTooltip(value, formatReliability(value.parsed.y)),
    });
  } else {
    set(options, "options.plugins.tooltip.callbacks", {
      label: (value) => formatTooltip(value, value.parsed.y),
    });
  }

  return options;
};

// A wrapper to pass through options to the interactive chart
const InteractiveChart = ({ series, label, metric, chartType }: Props) => (
  <Chart
    height="350px"
    label={label}
    metric={metric}
    series={series}
    options={chartOptions(metric)}
    chartType={chartType}
  />
);

export default InteractiveChart;
